import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'shared/api/client';
import { bookmarkKeys } from './keys';

export const useBookmarks = (documentSuperId: number | string) => {
  const queryClient = useQueryClient();

  const { data: bookmarks, isLoading: isBookmarksLoading } = useQuery({
    queryKey: bookmarkKeys.list(documentSuperId),

    queryFn: async () => {
      const { data } = await api.GET('/bookmarks/{documentId}', { params: { path: { documentId: String(documentSuperId) } } });
      // v5 removed query onSuccess; invalidating from the fetch keeps links refreshing on every load.
      if (documentSuperId) {
        queryClient.invalidateQueries({
          queryKey: bookmarkKeys.withLinks(documentSuperId),
        });
      }
      return data;
    },

    enabled: !!documentSuperId,
  });

  const { data: bookmarkAndLinks, isLoading: isBookmarksAndLinksLoading } = useQuery({
    queryKey: bookmarkKeys.withLinks(documentSuperId),

    queryFn: async () => {
      const { data } = await api.GET('/bookmarks/links/{documentId}', { params: { path: { documentId: String(documentSuperId) } } });
      return data;
    },

    enabled: !!documentSuperId,
    placeholderData: keepPreviousData,
  });

  const createLink = useMutation({
    mutationFn: async ({
      linkId,
      documentSuperId,
      sectionId,
      label,
      bookmarkId,
    }: {
      linkId: string;
      documentSuperId: number | string;
      sectionId: string;
      label: string;
      bookmarkId: string;
    }) => {
      await api.POST('/bookmarks/link', { body: { linkId, documentSuperId: Number(documentSuperId), sectionId, label, bookmarkId } });
    },

    onSuccess: (_, { documentSuperId }) => {
      queryClient.invalidateQueries({
        queryKey: bookmarkKeys.withLinks(documentSuperId),
      });
    },
  });

  const deleteLink = useMutation({
    mutationFn: async ({ id }: { id: string; documentSuperId: number | string }) => {
      await api.DELETE('/bookmarks/link/{id}', { params: { path: { id } } });
    },

    onSuccess: (_, { documentSuperId }) => {
      queryClient.invalidateQueries({
        queryKey: bookmarkKeys.withLinks(documentSuperId),
      });
      queryClient.invalidateQueries({
        queryKey: bookmarkKeys.list(documentSuperId),
      });
    },
  });

  const createBookmark = useMutation({
    mutationFn: async ({
      bookmarkId,
      label,
      documentSuperId,
      sectionId,
    }: {
      bookmarkId: string;
      label: string;
      documentSuperId: number | string;
      sectionId: string;
    }) => {
      await api.POST('/bookmarks/', { body: { bookmarkId, label, documentSuperId: Number(documentSuperId), sectionId } });
    },

    onSuccess: (_, { documentSuperId }) => {
      queryClient.invalidateQueries({
        queryKey: bookmarkKeys.withLinks(documentSuperId),
      });
      queryClient.invalidateQueries({
        queryKey: bookmarkKeys.list(documentSuperId),
      });
    },
  });

  const deleteBookmark = useMutation({
    mutationFn: async ({ id }: { id: string; documentSuperId: number | string }) => {
      await api.DELETE('/bookmarks/{id}', { params: { path: { id } } });
    },

    onSuccess: (_, { documentSuperId }) => {
      queryClient.invalidateQueries({
        queryKey: bookmarkKeys.withLinks(documentSuperId),
      });
      queryClient.invalidateQueries({
        queryKey: bookmarkKeys.list(documentSuperId),
      });
    },
  });

  const updateLink = useMutation({
    mutationFn: async ({ id, bookmarkId }: { id: string; bookmarkId: string; documentSuperId?: number | string }) => {
      await api.PUT('/bookmarks/link/{id}', { params: { path: { id } }, body: { bookmarkId } });
    },

    onSuccess: (_, { documentSuperId }) => {
      queryClient.invalidateQueries({
        queryKey: bookmarkKeys.withLinks(documentSuperId),
      });
    },
  });

  return {
    bookmarks,
    isBookmarksLoading,
    bookmarkAndLinks,
    isBookmarksAndLinksLoading,

    createBookmark: createBookmark.mutate,
    deleteBookmark: deleteBookmark.mutate,
    createLink: createLink.mutate,
    deleteLink: deleteLink.mutate,
    updateLink: updateLink.mutate,
  };
};
